import { useState, useMemo, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { X, Plus, AlertCircle, CheckCircle2, MailX } from "lucide-react";
import { useTranslation } from "@/hooks/useTranslation";
import { publicEventRequestConfig, type PublicEvent } from "@/services/api/publicEvents";
import { canTrackRsvpFunnel, trackRsvpFunnel } from "@/services/api/rsvpFunnel";
import type { HostSurfaceConfig } from "@/lib/event-templates";
import { rsvpDialogCssVars } from "./rsvpDialogTheme";
import axios from "axios";
import { toast } from "sonner";

const API_BASE_URL = import.meta.env.VITE_API_URL || "https://dev.wedistar.com/api";

interface PublicRsvpFormProps {
  event: PublicEvent;
  config: HostSurfaceConfig;
  locale: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type GuestFormData = {
  name: string;
  type: "adult" | "kid";
  menuPreference?: "standard" | "vegetarian" | "vegan";
  accommodationNeeded: boolean;
};

type FormErrors = {
  email?: string;
  phone?: string;
  familyName?: string;
  guests?: Record<number, { name?: string; type?: string; menuPreference?: string }>;
  general?: string;
  consent?: string;
};

const RATE_LIMIT_KEY = "rsvp_validation_attempts";
const RATE_LIMIT_MAX = 20; // Soft guard - warning only
const RATE_LIMIT_WINDOW = 60000; // 1 minute

type SubmittedSummary = {
  email: string;
  guestCount: number;
  // JUB-68: false when the family said "we cannot come" (older stored
  // summaries predate the flag and are treated as attending)
  attending?: boolean;
};

// JUB-101: the dialog is themed by scoping the couple's EventTemplateConfig
// to CSS custom properties on the dialog root, then letting these rules
// retarget the existing shadcn utility classes (text-muted-foreground,
// text-destructive, bg-background, ...) — no per-element style prop needed
// for the bulk of the form, and any current or future template "just works"
// as long as its config resolves through ensureAccessibleText/-ErrorColor.
const THEMED_DIALOG_STYLES = `
  .jub-rsvp-themed { font-family: var(--rsvp-body-font); }
  .jub-rsvp-themed.rsvp-dialog-surface {
    background: var(--rsvp-surface);
    color: var(--rsvp-text);
    border-color: var(--rsvp-border);
    box-shadow: var(--rsvp-shadow);
  }
  .jub-rsvp-themed .rsvp-dialog-title { font-family: var(--rsvp-heading-font); }
  .jub-rsvp-themed .text-muted-foreground { color: var(--rsvp-muted); }
  .jub-rsvp-themed .text-destructive { color: var(--rsvp-error); }
  .jub-rsvp-themed input,
  .jub-rsvp-themed select,
  .jub-rsvp-themed textarea {
    background: var(--rsvp-surface);
    border-color: var(--rsvp-border);
    color: var(--rsvp-text);
  }
  .jub-rsvp-themed input.border-destructive,
  .jub-rsvp-themed select.border-destructive,
  .jub-rsvp-themed textarea.border-destructive {
    border-color: var(--rsvp-error);
  }
  .jub-rsvp-themed input::placeholder,
  .jub-rsvp-themed textarea::placeholder { color: var(--rsvp-muted); }
  .jub-rsvp-themed input[type="checkbox"] { accent-color: var(--rsvp-accent); }
  .jub-rsvp-themed .rsvp-submit-btn { background: var(--rsvp-submit-bg); color: var(--rsvp-submit-text); }
  .jub-rsvp-themed .rsvp-submit-btn:hover { background: var(--rsvp-submit-bg); filter: brightness(0.92); }
  .jub-rsvp-themed .rsvp-cancel-btn {
    background: var(--rsvp-surface);
    border-color: var(--rsvp-border);
    color: var(--rsvp-text);
  }
  .jub-rsvp-themed a { color: var(--rsvp-link); text-decoration: underline; text-underline-offset: 2px; }
`;

const submittedStorageKey = (eventId: string) => `rsvp_submitted_${eventId}`;

function readSubmittedSummary(eventId: string): SubmittedSummary | null {
  try {
    const raw = localStorage.getItem(submittedStorageKey(eventId));
    return raw ? (JSON.parse(raw) as SubmittedSummary) : null;
  } catch {
    return null;
  }
}

// JUB-67: recover typed-but-unsubmitted answers after a page reload (mobile
// tab evicted from memory, pull-to-refresh, mis-tap on back) without
// building a second storage scheme — this follows the `rsvp_submitted_*`
// precedent above, just keyed for an in-flight draft instead of a finished
// one.
type CeremonyAttendanceDraft = {
  civil: boolean;
  religious: boolean;
  baptism?: boolean;
  party: boolean;
};

type PersistedDraft = {
  email: string;
  phone: string;
  familyName: string;
  notes: string;
  attending: boolean;
  ceremonyAttendance: CeremonyAttendanceDraft;
  guests: GuestFormData[];
  savedAt: number;
};

const draftStorageKey = (eventId: string) => `rsvp_draft_${eventId}`;

// 30 minutes: long enough to survive "lost signal, walked outside, came back"
// but short enough that a draft left behind on a shared/venue device (family
// passes the phone around, or a kiosk tablet) does not sit there indefinitely
// waiting to leak into the next guest's RSVP.
const DRAFT_TTL_MS = 30 * 60 * 1000;

function readDraft(eventId: string): PersistedDraft | null {
  try {
    const raw = localStorage.getItem(draftStorageKey(eventId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedDraft;
    if (!parsed || typeof parsed.savedAt !== "number" || Date.now() - parsed.savedAt > DRAFT_TTL_MS) {
      localStorage.removeItem(draftStorageKey(eventId));
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function clearDraft(eventId: string) {
  try {
    localStorage.removeItem(draftStorageKey(eventId));
  } catch {
    // Storage unavailable (private mode) — nothing to clear
  }
}

function writeDraft(eventId: string, draft: Omit<PersistedDraft, "savedAt">) {
  try {
    localStorage.setItem(draftStorageKey(eventId), JSON.stringify({ ...draft, savedAt: Date.now() }));
  } catch {
    // Storage unavailable (private mode) — the draft simply won't survive a reload this visit
  }
}

export function PublicRsvpForm({
  event,
  config,
  locale,
  open,
  onOpenChange,
}: PublicRsvpFormProps) {
  const { t } = useTranslation();
  // JUB-59: read once, before the draft below, so a completed RSVP never
  // resurrects an in-flight draft for the same event.
  const [submitted, setSubmitted] = useState<SubmittedSummary | null>(() =>
    readSubmittedSummary(event.uuid)
  );
  // JUB-67: restore an in-flight draft after reload — but never once this
  // event's RSVP is already submitted, and never past DRAFT_TTL_MS (see
  // above). Read once on mount; later saves just overwrite localStorage.
  const [draftOnMount] = useState<PersistedDraft | null>(() =>
    submitted ? null : readDraft(event.uuid)
  );
  const [draftRestored, setDraftRestored] = useState(!!draftOnMount);
  const [email, setEmail] = useState(draftOnMount?.email ?? event.invitedGuest?.email ?? "");
  const [phone, setPhone] = useState(draftOnMount?.phone ?? event.invitedGuest?.phone ?? "");
  const [familyName, setFamilyName] = useState(
    draftOnMount?.familyName ?? event.invitedGuest?.familyName ?? ""
  );
  const [notes, setNotes] = useState(draftOnMount?.notes ?? "");
  // JUB-105: GDPR consent — required before the form can be submitted.
  // JUB-67: deliberately NOT restored from the draft — consent is recorded
  // server-side with its own version + timestamp, so it must be re-affirmed
  // on every submission rather than silently carried over from a reload (or,
  // on a shared device, from whoever typed the draft in the first place).
  const [consent, setConsent] = useState(false);
  const noticeLocale = locale === "ro" || locale === "en" ? locale : "ro";
  const noticePath = `/${noticeLocale}/data-processing-notice`;

  // JUB-101: honour the couple's template design instead of the plain
  // shadcn defaults — palette, typography and button style, per template.
  // Every color rendered as text is routed through the WCAG helpers so the
  // dialog stays >= 4.5:1 regardless of which template config is passed in.
  // JUB-161: read through a safe accessor; a config missing a field (an
  // installed package, a partial draft) must never blank the public page.
  const dialogCssVars = rsvpDialogCssVars(config);
  // JUB-70: guests who ignored the checkboxes used to submit all-false
  // attendance — recorded as 'confirmed' but attending nothing. Every
  // enabled ceremony now starts checked; guests untick what they skip.
  const defaultCeremonyAttendance = () => ({
    civil: !!event.ceremonies?.civil?.enabled,
    religious: !!event.ceremonies?.religious?.enabled,
    baptism: !!event.ceremonies?.baptism?.enabled,
    party: !!event.ceremonies?.party?.enabled,
  });
  const [ceremonyAttendance, setCeremonyAttendance] = useState<CeremonyAttendanceDraft>(
    () => draftOnMount?.ceremonyAttendance ?? defaultCeremonyAttendance()
  );
  // JUB-68: explicit "Vin / Nu pot ajunge" answer. Attending is the default so
  // the classic confirm flow is unchanged; a decline is stored as 'declined'
  // and skips ceremonies, menu and accommodation.
  const [attending, setAttending] = useState(draftOnMount?.attending ?? true);
  const [guests, setGuests] = useState<GuestFormData[]>(
    draftOnMount?.guests && draftOnMount.guests.length > 0
      ? draftOnMount.guests
      : [
          {
            name: event.invitedGuest?.name ?? "",
            type: "adult",
            accommodationNeeded: false,
          },
        ]
  );

  // JUB-225: pre-fill invitedGuest context if loaded asynchronously and no draft is active
  const invitedGuestAppliedRef = useRef(false);
  useEffect(() => {
    if (draftOnMount || submitted || invitedGuestAppliedRef.current || !event.invitedGuest) {
      return;
    }
    invitedGuestAppliedRef.current = true;
    const g = event.invitedGuest;
    if (g.email) setEmail((prev) => prev || g.email || "");
    if (g.phone) setPhone((prev) => prev || g.phone || "");
    if (g.familyName) setFamilyName((prev) => prev || g.familyName || "");
    if (g.name) {
      setGuests((prev) => {
        if (prev.length === 1 && !prev[0].name.trim()) {
          return [{ ...prev[0], name: g.name || "" }];
        }
        return prev;
      });
    }
  }, [event.invitedGuest, draftOnMount, submitted]);
  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState(false);
  const [rateLimitWarning, setRateLimitWarning] = useState(false);
  // JUB-59: keep the confirmation visible across dialog reopen and page reload
  // instead of a 3-second toast that reverts the page to "RSVP ACUM".
  // (submitted itself is declared above, before the draft restore.)

  const rsvpSettings = event.rsvpSettings;

  // Count enabled meal options - must be called before early return
  const enabledMealOptions = useMemo(() => {
    const options: Array<"standard" | "vegetarian" | "vegan"> = [];
    if (rsvpSettings?.mealOptions.standard) options.push("standard");
    if (rsvpSettings?.mealOptions.vegetarian) options.push("vegetarian");
    if (rsvpSettings?.mealOptions.vegan) options.push("vegan");
    return options;
  }, [rsvpSettings?.mealOptions]);

  // JUB-68: none of the attendance details apply to a family that is not coming
  const showMealPreferences = attending && enabledMealOptions.length >= 1; // JUB-22: show even with 1 option enabled
  const showAccommodation = attending && (rsvpSettings?.accommodationEnabled ?? false);
  const showKidsToggle = attending && (rsvpSettings?.kidsEnabled ?? false);
  // JUB-225: support all 4 enabled ceremonies (civil, religious, baptism, party).
  const attendanceCeremonies = Object.entries(event.ceremonies ?? {}).filter(
    ([, ceremony]) => ceremony?.enabled,
  );
  const showCeremonies = attending && attendanceCeremonies.length > 0;

  // Rate limiting check
  const checkRateLimit = (): boolean => {
    try {
      const stored = sessionStorage.getItem(RATE_LIMIT_KEY);
      const data = stored ? JSON.parse(stored) : { count: 0, resetAt: Date.now() + RATE_LIMIT_WINDOW };

      if (Date.now() > data.resetAt) {
        data.count = 0;
        data.resetAt = Date.now() + RATE_LIMIT_WINDOW;
      }

      if (data.count >= RATE_LIMIT_MAX) {
        setRateLimitWarning(true);
        return true; // Soft guard - allow but warn
      }

      data.count++;
      sessionStorage.setItem(RATE_LIMIT_KEY, JSON.stringify(data));
      return false;
    } catch {
      return false;
    }
  };

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Email validation
    if (!email) {
      newErrors.email = t("validation.emailRequired");
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = t("validation.emailInvalid");
    }

    // Guest validation — every visible guest card must have a name (JUB-69:
    // blank cards used to be silently dropped from the submission)
    const namedGuests = guests.filter((g) => g.name && g.name.trim().length > 0);
    if (namedGuests.length === 0) {
      newErrors.general = t("validation.atLeastOneGuest");
    }

    // Check at least one adult (only if kids toggle is enabled, otherwise all are adults)
    if (showKidsToggle) {
      const adultCount = namedGuests.filter((g) => g.type === "adult").length;
      if (adultCount === 0) {
        newErrors.general = t("validation.atLeastOneAdult");
      }
    }

    // Validate each guest card, including blank ones
    const guestErrors: Record<number, { name?: string; type?: string; menuPreference?: string }> = {};
    guests.forEach((guest, index) => {
      if (!guest.name.trim()) {
        guestErrors[index] = { ...guestErrors[index], name: t("validation.nameRequired") };
      }
      if (guest.menuPreference && !enabledMealOptions.includes(guest.menuPreference)) {
        guestErrors[index] = { ...guestErrors[index], menuPreference: t("validation.invalidMealPreference") };
      }
    });

    if (Object.keys(guestErrors).length > 0) {
      newErrors.guests = guestErrors;
    }

    // JUB-105: GDPR consent required before submitting.
    if (!consent) {
      newErrors.consent = t("validation.consentRequired");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e?: React.FormEvent) => {
    // Prevent default form submission if event is provided
    if (e) {
      e.preventDefault();
    }

    // Check rate limit
    checkRateLimit();

    // JUB-60: the button is always tappable — validation runs here and renders
    // inline errors instead of silently disabling the submit button.
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      // Filter out empty guests
      const validGuests = guests
        .filter((g) => g.name.trim())
        .map((g) => ({
          name: g.name.trim(),
          // The kid toggle is hidden on a decline (JUB-68) or when kids are
          // disabled — everyone submits as adult then, matching the API rule.
          type: showKidsToggle ? g.type : "adult",
          menuPreference: showMealPreferences ? g.menuPreference : undefined,
          accommodationNeeded: showAccommodation ? g.accommodationNeeded : false,
        }));

      const response = await axios.post(
        `${API_BASE_URL}/events/public/${event.url || event.uuid}/rsvp`,
        {
          email: email.trim(),
          phone: phone.trim() || undefined,
          familyName: familyName.trim() || undefined,
          notes: notes.trim() || undefined,
          attending,
          guests: validGuests,
          // JUB-68: a decline attends nothing — do not send the default-checked flags
          ceremonyAttendance: attending ? ceremonyAttendance : undefined,
          // JUB-105: GDPR consent, recorded server-side with a version + timestamp.
          consent,
          // JUB-67: lets the API count this submission in the interaction
          // counts — only when the guest granted analytics consent.
          analyticsConsent: canTrackRsvpFunnel(),
        },
        {
          headers: {
            ...publicEventRequestConfig(event.url || event.uuid).headers,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data) {
        toast.success(t("rsvp.submitted"));
        const summary: SubmittedSummary = {
          email: email.trim(),
          guestCount: guests.filter((g) => g.name.trim()).length,
          attending,
        };
        // JUB-59: persist confirmation so reopening the dialog (or reloading
        // the page) shows the success state instead of an empty form whose
        // resubmission the API rejects with 422.
        try {
          localStorage.setItem(submittedStorageKey(event.uuid), JSON.stringify(summary));
        } catch {
          // Storage unavailable (private mode) — in-memory state still covers this visit
        }
        // JUB-67: the draft's job is done — clear it so it cannot resurface
        // (e.g. a later visitor on the same shared device reopening this
        // event's RSVP) and does not fight with the success panel above.
        clearDraft(event.uuid);
        setDraftRestored(false);
        setSubmitted(summary);
        // Reset form
        setEmail("");
        setPhone("");
        setFamilyName("");
        setNotes("");
        setConsent(false);
        setAttending(true);
        setCeremonyAttendance(defaultCeremonyAttendance());
        setGuests([
          {
            name: "",
            type: "adult",
            accommodationNeeded: false,
          },
        ]);
        setErrors({});
      }
    } catch (error: unknown) {
      console.error("RSVP submission error:", error);
      let errorMessage: string | null = null;
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { data?: { error?: { message?: string; code?: string } } }; message?: string };
        errorMessage = axiosError.response?.data?.error?.message || 
                      axiosError.response?.data?.error?.code || 
                      axiosError.message || 
                      null;
      } else if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
        errorMessage = error.message;
      }

      const axiosError = error && typeof error === 'object' && 'response' in error 
        ? error as { response?: { status?: number; data?: { errors?: Record<string, string[]>; error?: { code?: string; message?: string } } } }
        : null;
      const errorCode = axiosError?.response?.data?.error?.code;
      // Server error codes with a localized inline message. These are shown
      // inline ONLY — the untranslated server fallback string used to be
      // toasted next to the RO inline text (JUB-87 dev validation, JUB-70 banner).
      const localizedByCode: Record<string, string> = {
        email_already_used: t("rsvp.emailAlreadyUsed"),
        rsvp_no_ceremony_selected: t("rsvp.noCeremonySelected"),
        rsvp_guest_limit_exceeded: t("rsvp.guestLimitExceeded"),
      };
      const localizedMessage = errorCode ? localizedByCode[errorCode] : undefined;
      if (!localizedMessage) {
        toast.error(errorMessage || t("rsvp.submitError"));
      }

      if (errorCode === "email_already_used") {
        // Must be checked BEFORE the generic 422 branch: the API sends this as
        // 422 with an { error } envelope (not Laravel's { errors }), so the
        // generic branch used to swallow it and show nothing (JPI: Oana pilot).
        // JUB-87 (PO decision): guests cannot edit their RSVP themselves, so the
        // copy points to the organizers instead of a self-service flow.
        setErrors({ email: localizedMessage });
      } else if (localizedMessage) {
        setErrors({ general: localizedMessage });
      } else if (axiosError?.response?.status === 422) {
        // Validation errors — Laravel { errors } shape or { error } envelope
        const validationErrors = axiosError.response.data?.errors || {};
        const newErrors: FormErrors = {};

        if (validationErrors.email) {
          newErrors.email = Array.isArray(validationErrors.email) ? validationErrors.email[0] : validationErrors.email;
        }
        if (validationErrors.guests) {
          // Plain "guests" key carries the "at least one adult guest is
          // required" rule as a string[]; per-guest field errors arrive as
          // dotted keys (guests.0.name, guests.0.menuPreference) instead,
          // parsed into the per-index shape below.
          const guestsMessage = Array.isArray(validationErrors.guests)
            ? validationErrors.guests[0]
            : validationErrors.guests;
          if (guestsMessage) newErrors.general = guestsMessage;
        }
        const guestFieldErrors: NonNullable<FormErrors["guests"]> = {};
        for (const [key, messages] of Object.entries(validationErrors)) {
          const match = /^guests\.(\d+)\.(name|type|menuPreference)$/.exec(key);
          if (!match) continue;
          const index = Number(match[1]);
          const field = match[2] as "name" | "type" | "menuPreference";
          const message = Array.isArray(messages) ? messages[0] : messages;
          if (!message) continue;
          guestFieldErrors[index] = { ...guestFieldErrors[index], [field]: message };
        }
        if (Object.keys(guestFieldErrors).length > 0) {
          newErrors.guests = guestFieldErrors;
        }
        if (validationErrors.general) {
          newErrors.general = Array.isArray(validationErrors.general) ? validationErrors.general[0] : validationErrors.general;
        }

        if (Object.keys(newErrors).length === 0) {
          // { error } envelope (non-Laravel shape) — surface its message inline
          // instead of silently clearing the form errors
          const envelopeMessage = axiosError.response.data?.error?.message;
          if (envelopeMessage) newErrors.general = envelopeMessage;
        }

        setErrors(newErrors);
      } else {
        setErrors({ general: errorMessage || t("rsvp.submitError") });
      }
    } finally {
      setLoading(false);
    }
  };

  // Add guest
  const addGuest = () => {
    setGuests([
      ...guests,
      {
        name: "",
        type: "adult",
        accommodationNeeded: false,
      },
    ]);
  };

  // Remove guest
  const removeGuest = (index: number) => {
    if (guests.length > 1) {
      setGuests(guests.filter((_, i) => i !== index));
      // Clear errors for removed guest
      if (errors.guests?.[index]) {
        const newGuestErrors = { ...errors.guests };
        delete newGuestErrors[index];
        setErrors({ ...errors, guests: Object.keys(newGuestErrors).length > 0 ? newGuestErrors : undefined });
      }
    }
  };

  // Update guest
  const updateGuest = (index: number, field: keyof GuestFormData, value: string | boolean | "adult" | "kid" | "standard" | "vegetarian" | "vegan" | undefined) => {
    const updatedGuests = [...guests];
    updatedGuests[index] = {
      ...updatedGuests[index],
      [field]: value,
    };
    setGuests(updatedGuests);

    // Clear error for this field
    if (errors.guests?.[index]?.[field as keyof typeof errors.guests[number]]) {
      const newGuestErrors = { ...errors.guests };
      if (newGuestErrors[index]) {
        delete newGuestErrors[index][field as keyof typeof newGuestErrors[number]];
        if (Object.keys(newGuestErrors[index]).length === 0) {
          delete newGuestErrors[index];
        }
      }
      setErrors({ ...errors, guests: Object.keys(newGuestErrors).length > 0 ? newGuestErrors : undefined });
    }
  };

  // Clear error when field changes
  const clearError = (field: keyof FormErrors) => {
    if (errors[field]) {
      const newErrors = { ...errors };
      delete newErrors[field];
      setErrors(newErrors);
    }
  };

  // JUB-67: typed input is intentionally kept when the dialog closes — an
  // accidental dismiss (Escape, backdrop tap) no longer wipes the form.
  // Only transient warnings are cleared.
  useEffect(() => {
    if (!open) {
      setRateLimitWarning(false);
    }
  }, [open]);

  // JUB-67: interaction counts (only with analytics consent — see
  // trackRsvpFunnel). "opened" once per opening of the form, "field_touched"
  // once per page view on the first edit, "draft_restored" once when a draft
  // came back after a reload. Draft persistence above never depends on these.
  const funnelIdentifier = event.url || event.uuid;
  const fieldTouchedSent = useRef(false);
  const draftRestoredSent = useRef(false);
  const openedSent = useRef(false);
  useEffect(() => {
    if (!open) {
      openedSent.current = false;
      return;
    }
    if (submitted || openedSent.current) return;
    openedSent.current = true;
    trackRsvpFunnel(funnelIdentifier, "opened");
    if (draftOnMount && !draftRestoredSent.current) {
      draftRestoredSent.current = true;
      trackRsvpFunnel(funnelIdentifier, "draft_restored");
    }
    // `submitted` is read only at the moment the form opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, funnelIdentifier, draftOnMount]);
  const handleFieldInteraction = () => {
    if (fieldTouchedSent.current) return;
    fieldTouchedSent.current = true;
    trackRsvpFunnel(funnelIdentifier, "field_touched");
  };

  // JUB-67: debounced draft save. Consent is intentionally excluded (see the
  // useState above) — everything else here is the same data the API accepts,
  // nothing extra. A blank draft (nothing typed, still on the "attending"
  // default) is not written at all, and any leftover draft is cleared —
  // otherwise every guest who opens and closes without typing would leave a
  // 30-minute-TTL localStorage entry behind for no reason.
  useEffect(() => {
    if (submitted) return;
    const hasContent =
      email.trim() !== "" ||
      phone.trim() !== "" ||
      familyName.trim() !== "" ||
      notes.trim() !== "" ||
      !attending ||
      guests.some((g) => g.name.trim() !== "");
    const timer = window.setTimeout(() => {
      if (hasContent) {
        writeDraft(event.uuid, { email, phone, familyName, notes, attending, ceremonyAttendance, guests });
      } else {
        clearDraft(event.uuid);
      }
    }, 400);
    return () => window.clearTimeout(timer);
  }, [event.uuid, submitted, email, phone, familyName, notes, attending, ceremonyAttendance, guests]);

  // JUB-67: Cancel is an explicit "I'm done" signal — unlike an accidental
  // dismiss (Escape/backdrop, handled by onOpenChange alone, which keeps the
  // draft for recovery), so it also clears the saved draft. This matters on
  // a shared/venue device: one family cancels, hands the phone to the next
  // one, and the next family must not see the previous family's answers.
  const handleCancelClick = () => {
    clearDraft(event.uuid);
    setDraftRestored(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <style>{THEMED_DIALOG_STYLES}</style>
      <DialogContent
        className="jub-rsvp-themed rsvp-dialog-surface w-[90vw] max-w-6xl max-h-[90vh] overflow-y-auto"
        style={dialogCssVars}
        onChangeCapture={submitted ? undefined : handleFieldInteraction}
      >
        <DialogHeader>
          <DialogTitle className="rsvp-dialog-title">
            {submitted
              ? submitted.attending === false
                ? t("rsvp.declinedTitle")
                : t("rsvp.successTitle")
              : t("rsvp.title")}
          </DialogTitle>
        </DialogHeader>

        {submitted ? (
          <>
            <div
              className="flex flex-col items-center gap-3 py-6 text-center"
              data-testid="rsvp-success-panel"
              data-attending={submitted.attending === false ? "false" : "true"}
            >
              {submitted.attending === false ? (
                // JUB-68: decline recorded — no email is sent (JUB-87 PO decision),
                // so the copy only says the answer was noted.
                <>
                  <MailX className="h-12 w-12 text-muted-foreground" aria-hidden="true" />
                  <p className="font-medium">
                    {t("rsvp.declinedBody", { email: submitted.email })}
                  </p>
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-12 w-12 text-green-600" aria-hidden="true" />
                  <p className="font-medium">
                    {t("rsvp.successBody", {
                      guestCount: String(submitted.guestCount),
                      email: submitted.email,
                    })}
                  </p>
                </>
              )}
              <p className="text-sm text-muted-foreground">{t("rsvp.successChangeNote")}</p>
            </div>
            <DialogFooter>
              <Button className="rsvp-submit-btn" onClick={() => onOpenChange(false)}>
                {t("common.close")}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
        {/* JUB-67: told, not silently done to them — a guest whose answers
            came back after a reload should know why the form is already
            filled in, especially since consent below is NOT part of it and
            still needs a fresh tick. role="status" (polite) rather than the
            Alert default of role="alert" (assertive): this is informational,
            not an error, and should not interrupt a screen reader mid-typing. */}
        {draftRestored && (
          <Alert role="status" data-testid="rsvp-draft-restored">
            <AlertDescription className="flex items-center justify-between gap-3">
              <span>{t("rsvp.draftRestored")}</span>
              <button
                type="button"
                onClick={() => setDraftRestored(false)}
                className="text-sm underline shrink-0"
              >
                {t("common.close")}
              </button>
            </AlertDescription>
          </Alert>
        )}

        {rateLimitWarning && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {t("rsvp.rateLimitWarning")}
            </AlertDescription>
          </Alert>
        )}

        {errors.general && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{errors.general}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-6 py-4">
          {/* JUB-68: explicit attending yes/no — the first thing a guest answers */}
          <div className="space-y-3">
            <h3 className="font-semibold" id="rsvp-attending-label">
              {t("rsvp.attending")}
            </h3>
            <div
              role="radiogroup"
              aria-labelledby="rsvp-attending-label"
              className="grid grid-cols-1 sm:grid-cols-2 gap-3"
              data-testid="rsvp-attending-choice"
            >
              <button
                type="button"
                role="radio"
                aria-checked={attending}
                data-testid="rsvp-attending-yes"
                onClick={() => setAttending(true)}
                className={`rounded-lg border-2 px-4 py-3 text-left text-sm font-medium transition-colors ${
                  attending
                    ? "border-green-600 bg-green-50 text-green-900"
                    : "border-input bg-background text-foreground hover:bg-muted"
                }`}
              >
                <span className="block text-base">{t("rsvp.attendingYes")}</span>
                <span className="block text-xs font-normal text-muted-foreground">
                  {t("rsvp.attendingYesHint")}
                </span>
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={!attending}
                data-testid="rsvp-attending-no"
                onClick={() => setAttending(false)}
                className={`rounded-lg border-2 px-4 py-3 text-left text-sm font-medium transition-colors ${
                  !attending
                    ? "border-destructive bg-red-50 text-red-900"
                    : "border-input bg-background text-foreground hover:bg-muted"
                }`}
              >
                <span className="block text-base">{t("rsvp.attendingNo")}</span>
                <span className="block text-xs font-normal text-muted-foreground">
                  {t("rsvp.attendingNoHint")}
                </span>
              </button>
            </div>
          </div>

          {/* Contact Information */}
          <div className="space-y-4">
            <h3 className="font-semibold">{t("rsvp.contactInformation")}</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="email">
                  {t("rsvp.email")} <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  inputMode="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    clearError("email");
                  }}
                  className={errors.email ? "border-destructive" : ""}
                  aria-invalid={!!errors.email}
                  aria-describedby={errors.email ? "email-error" : "email-help"}
                />
                {/* JUB-90 (WCAG 3.3.1): role="alert" announces validation errors
                    to screen reader users instead of leaving them silent */}
                {errors.email && (
                  <p id="email-error" role="alert" className="text-sm text-destructive mt-1">
                    {errors.email}
                  </p>
                )}
                {/* JUB-66: explain why the email is required */}
                {!errors.email && (
                  <p id="email-help" className="text-sm text-muted-foreground mt-1">
                    {attending ? t("rsvp.emailHelp") : t("rsvp.emailHelpDecline")}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="phone">{t("rsvp.phone")}</Label>
                <Input
                  id="phone"
                  type="tel"
                  autoComplete="tel"
                  inputMode="tel"
                  value={phone}
                  onChange={(e) => {
                    setPhone(e.target.value);
                    clearError("phone");
                  }}
                  className={errors.phone ? "border-destructive" : ""}
                  aria-invalid={!!errors.phone}
                  aria-describedby={errors.phone ? "phone-error" : undefined}
                />
                {errors.phone && (
                  <p id="phone-error" role="alert" className="text-sm text-destructive mt-1">
                    {errors.phone}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="familyName">{t("guests.familyName")}</Label>
                <Input
                  id="familyName"
                  type="text"
                  autoComplete="family-name"
                  value={familyName}
                  onChange={(e) => {
                    setFamilyName(e.target.value);
                    clearError("familyName");
                  }}
                  className={errors.familyName ? "border-destructive" : ""}
                  aria-invalid={!!errors.familyName}
                  aria-describedby={errors.familyName ? "familyName-error" : undefined}
                />
                {errors.familyName && (
                  <p id="familyName-error" role="alert" className="text-sm text-destructive mt-1">
                    {errors.familyName}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Ceremony Attendance - Shared for entire RSVP */}
          {showCeremonies && event.ceremonies && (
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold">{t("rsvp.ceremonyAttendance")}</h3>
                {/* JUB-70: enabled ceremonies start checked — tell guests why */}
                <p className="text-sm text-muted-foreground mt-1">{t("rsvp.ceremonyAttendanceHint")}</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {attendanceCeremonies.map(([key, ceremony]) => {
                  if (!ceremony?.enabled) return null;
                  return (
                    <div key={key} className="flex items-center space-x-2">
                      {/* JUB-90 (WCAG 2.5.8): 24x24px minimum target size */}
                      <input
                        type="checkbox"
                        id={`ceremony-${key}`}
                        checked={Boolean(ceremonyAttendance[key as keyof typeof ceremonyAttendance])}
                        onChange={(e) =>
                          setCeremonyAttendance({
                            ...ceremonyAttendance,
                            [key]: e.target.checked,
                          })
                        }
                        className="h-6 w-6 shrink-0 rounded border-gray-300 cursor-pointer"
                      />
                      <Label htmlFor={`ceremony-${key}`} className="font-normal cursor-pointer">
                        {ceremony.name}
                      </Label>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Guests */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold">{t("rsvp.guests") || t("guests.title")}</h3>
              <Button type="button" variant="outline" size="sm" onClick={addGuest}>
                <Plus className="h-4 w-4 mr-2" />
                {t("rsvp.addGuest") || t("guests.add")}
              </Button>
            </div>
            {/* JUB-68: names are still collected on a decline so the hosts know who is not coming */}
            {!attending && (
              <p className="text-sm text-muted-foreground">{t("rsvp.declineGuestsHint")}</p>
            )}

            {guests.map((guest, index) => (
              <div key={index} className="border rounded-lg p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium">
                    {t("rsvp.guest")} {index + 1}
                  </h4>
                  {guests.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeGuest(index)}
                      aria-label={t("rsvp.removeGuest", { number: index + 1 })}
                    >
                      <X className="h-4 w-4" aria-hidden="true" />
                    </Button>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor={`guest-${index}-name`}>
                      {t("rsvp.name")} <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id={`guest-${index}-name`}
                      type="text"
                      autoComplete={index === 0 ? "name" : "off"}
                      value={guest.name}
                      onChange={(e) => updateGuest(index, "name", e.target.value)}
                      className={errors.guests?.[index]?.name ? "border-destructive" : ""}
                      aria-invalid={!!errors.guests?.[index]?.name}
                      aria-describedby={errors.guests?.[index]?.name ? `guest-${index}-name-error` : undefined}
                    />
                    {errors.guests?.[index]?.name && (
                      <p id={`guest-${index}-name-error`} role="alert" className="text-sm text-destructive mt-1">
                        {errors.guests[index].name}
                      </p>
                    )}
                  </div>

                  {showKidsToggle && (
                    <div>
                      <Label htmlFor={`guest-${index}-type`}>{t("rsvp.type")}</Label>
                      <select
                        id={`guest-${index}-type`}
                        value={guest.type}
                        onChange={(e) => updateGuest(index, "type", e.target.value as "adult" | "kid")}
                        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      >
                        <option value="adult">{t("guests.adult")}</option>
                        <option value="kid">{t("guests.kid")}</option>
                      </select>
                    </div>
                  )}

                  {showMealPreferences && (
                    <div>
                      <Label htmlFor={`guest-${index}-menu`}>{t("rsvp.menuPreference")}</Label>
                      <select
                        id={`guest-${index}-menu`}
                        value={guest.menuPreference || ""}
                        onChange={(e) => updateGuest(index, "menuPreference", e.target.value || undefined)}
                        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                        aria-invalid={!!errors.guests?.[index]?.menuPreference}
                        aria-describedby={
                          errors.guests?.[index]?.menuPreference ? `guest-${index}-menu-error` : undefined
                        }
                      >
                        <option value="">{t("rsvp.selectMenuPreference")}</option>
                        {enabledMealOptions.map((option) => (
                          <option key={option} value={option}>
                            {t(`rsvp.menu.${option}`) || option.charAt(0).toUpperCase() + option.slice(1)}
                          </option>
                        ))}
                      </select>
                      {errors.guests?.[index]?.menuPreference && (
                        <p id={`guest-${index}-menu-error`} role="alert" className="text-sm text-destructive mt-1">
                          {errors.guests[index].menuPreference}
                        </p>
                      )}
                    </div>
                  )}

                  {showAccommodation && (
                    <div className="flex items-center space-x-2 pt-6">
                      {/* JUB-90 (WCAG 2.5.8): 24x24px minimum target size */}
                      <input
                        type="checkbox"
                        id={`guest-${index}-accommodation`}
                        checked={guest.accommodationNeeded}
                        onChange={(e) => updateGuest(index, "accommodationNeeded", e.target.checked)}
                        className="h-6 w-6 shrink-0 rounded border-gray-300 cursor-pointer"
                      />
                      <Label htmlFor={`guest-${index}-accommodation`} className="font-normal cursor-pointer">
                        {t("rsvp.accommodationNeeded")}
                      </Label>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* JUB-58: optional message / dietary restrictions */}
          <div className="space-y-2">
            <Label htmlFor="rsvp-notes">
              {attending ? t("rsvp.notesLabel") : t("rsvp.notesLabelDecline")}
            </Label>
            <Textarea
              id="rsvp-notes"
              value={notes}
              maxLength={500}
              rows={3}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={attending ? t("rsvp.notesPlaceholder") : t("rsvp.notesPlaceholderDecline")}
            />
          </div>

          {/* JUB-105: GDPR consent — required before submitting */}
          <div className="flex items-start space-x-2">
            <input
              type="checkbox"
              id="rsvp-consent"
              checked={consent}
              onChange={(e) => {
                setConsent(e.target.checked);
                if (errors.consent) {
                  setErrors((prev) => ({ ...prev, consent: undefined }));
                }
              }}
              className="mt-1 rounded border-gray-300"
              data-testid="rsvp-consent-checkbox"
            />
            <Label htmlFor="rsvp-consent" className="font-normal leading-relaxed">
              {t("rsvp.consent.text")}{" "}
              <Link to={noticePath} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                {t("rsvp.consent.link")}
              </Link>
              .
            </Label>
          </div>
          {errors.consent && (
            <p className="text-sm text-destructive mt-1">{errors.consent}</p>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            className="rsvp-cancel-btn"
            onClick={handleCancelClick}
            disabled={loading}
          >
            {t("common.cancel")}
          </Button>
          <Button
            type="button"
            className="rsvp-submit-btn"
            onClick={handleSubmit}
            disabled={loading}
            data-testid="rsvp-submit-button"
          >
            {loading ? t("common.loading") : attending ? t("rsvp.submit") : t("rsvp.submitDecline")}
          </Button>
        </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

